// Beta-0.5D-B · 保持期間 — Retention Overview (READ-ONLY).
// Aggregates DMS trash / procurement photos / backup / audit log / search index.

import { useEffect, useState } from 'react';
import axios from 'axios';
import { ArrowsClockwise, CircleNotch, Info } from '@phosphor-icons/react';
import { Button } from '@/components/ui/button';

const API = process.env.REACT_APP_BACKEND_URL;
const authHeader = () => {
  const t = localStorage.getItem('token');
  return t ? { Authorization: `Bearer ${t}` } : {};
};

const fmt = (n) => (typeof n === 'number' ? n.toLocaleString('ja-JP') : '—');
const fmtDT = (iso) => (iso ? new Date(iso).toLocaleString('ja-JP', { timeZone: 'Asia/Tokyo' }) : '—');

export default function RetentionOverviewPage() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/api/retention/overview`, { headers: authHeader() });
      setData(r.data);
    } catch (e) { /* handled inline */ }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  return (
    <div className="mx-auto max-w-5xl px-4 py-6 space-y-5" data-testid="retention-page">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold text-zinc-900">保持期間</h1>
          <p className="text-[12px] text-zinc-600 mt-1">
            各データの保持ルールと現状の件数を一覧で表示します（読み取り専用）。
            詳細な変更は「設定元」列のリンク先で行ってください。
          </p>
        </div>
        <Button variant="outline" onClick={load} disabled={loading} className="h-9 text-[12px]"
          data-testid="retention-refresh">
          {loading ? <CircleNotch size={13} className="animate-spin mr-1" />
                    : <ArrowsClockwise size={13} className="mr-1" />} 更新
        </Button>
      </div>

      <div className="rounded-lg border border-zinc-200 bg-white overflow-hidden">
        <table className="w-full text-[12px]">
          <thead className="bg-zinc-50 text-zinc-500">
            <tr className="[&>th]:px-3 [&>th]:py-2 [&>th]:text-left text-[10.5px] uppercase tracking-wider">
              <th className="w-40">データの種類</th>
              <th>保持期間</th>
              <th className="w-24">推定件数</th>
              <th className="w-28">最終削除</th>
              <th>所有者 / 設定元</th>
              <th>対象</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-zinc-100">
            {(data?.rows || []).map((r) => (
              <tr key={r.id} className="hover:bg-zinc-50/40"
                data-testid={`retention-row-${r.id}`}>
                <td className="px-3 py-3 font-semibold text-zinc-900">{r.data_source}</td>
                <td className="px-3 py-3 text-zinc-700 text-[11.5px]">
                  {r.current_retention_ja}
                  {r.current_retention_days && (
                    <span className="ml-1 tabular-nums font-mono text-zinc-500">
                      ({r.current_retention_days} 日)
                    </span>
                  )}
                </td>
                <td className="px-3 py-3 text-[13px] tabular-nums font-semibold text-teal-700">
                  {fmt(r.estimated_record_count)}
                </td>
                <td className="px-3 py-3 text-[10.5px] text-zinc-600 tabular-nums">
                  {r.last_purge_at ? (
                    <>
                      {fmtDT(r.last_purge_at)}
                      {typeof r.last_purge_count === 'number' && (
                        <span className="block text-zinc-400 text-[10px]">
                          -{r.last_purge_count.toLocaleString('ja-JP')} 件
                        </span>
                      )}
                    </>
                  ) : '—'}
                </td>
                <td className="px-3 py-3 text-[11.5px]">
                  <span className="text-zinc-900 font-medium">{r.owner}</span>
                  <span className="block text-[10.5px] text-zinc-500 mt-0.5">{r.configuration_source}</span>
                </td>
                <td className="px-3 py-3 text-[11px] text-zinc-600">{r.applies_to_ja}</td>
              </tr>
            ))}
            {!data && (
              <tr><td colSpan={6} className="px-3 py-8 text-center text-[11.5px] text-zinc-400"
                data-testid="retention-loading">
                {loading ? '読み込み中…' : 'データがありません'}
              </td></tr>
            )}
          </tbody>
        </table>
      </div>

      <div className="rounded-lg border border-zinc-200 bg-zinc-50 px-4 py-3 text-[11.5px] text-zinc-600"
        data-testid="retention-note">
        <p className="flex items-center gap-1"><Info size={12} />
          <span>この画面は表示のみ。保持期間ポリシーの変更は該当設定ページで行います。</span>
        </p>
      </div>
    </div>
  );
}
